/** Live state of generations that are streaming right now, fed by gen.* events. */
import { create } from "zustand";
import type { Message, MessageStatus, Part, Usage } from "./types";
import type { ServerEvent } from "./ws";

export interface Live {
  parts: Part[];
  seq: number;
  status: MessageStatus;
  error?: string | null;
  usage?: Usage;
  chatId?: string;
  tempId?: string;
}

interface LiveStore {
  live: Record<string, Live>;
  /** temp chat id -> message id of its latest generation */
  byTemp: Record<string, string>;
  seed: (m: Message) => void;
  /** Applies an event. Returns false when the client missed events and should reload the chat. */
  apply: (ev: ServerEvent) => boolean;
  forget: (messageId: string) => void;
}

function applyDelta(parts: Part[], index: number, kind: "text" | "reasoning", delta: string): Part[] | null {
  const next = parts.slice();
  if (index === next.length) {
    next.push({ type: kind, text: delta });
  } else if (index < next.length && (next[index].type === kind)) {
    const p = next[index] as { type: typeof kind; text: string };
    next[index] = { ...p, text: p.text + delta };
  } else {
    return null;
  }
  return next;
}

export const useLive = create<LiveStore>((set, get) => ({
  live: {},
  byTemp: {},
  seed: (m) => {
    if (m.status !== "streaming") return;
    const cur = get().live[m.id];
    if (cur && cur.seq >= m.seq) return;
    set((s) => ({ live: { ...s.live, [m.id]: { parts: m.parts, seq: m.seq, status: m.status, chatId: m.chat_id } } }));
  },
  apply: (ev) => {
    const id: string = ev.message_id;
    const cur = get().live[id];
    const base = { chatId: ev.chat_id, tempId: ev.temp_id };
    switch (ev.type) {
      case "gen.started":
        if (cur && cur.seq >= ev.seq) return true;
        set((s) => ({
          live: { ...s.live, [id]: { ...base, parts: [], seq: ev.seq, status: "streaming" } },
          byTemp: ev.temp_id ? { ...s.byTemp, [ev.temp_id]: id } : s.byTemp,
        }));
        return true;
      case "gen.delta": {
        if (!cur) return false;
        if (ev.seq <= cur.seq) return true;
        if (ev.seq !== cur.seq + 1) return false;
        const parts = applyDelta(cur.parts, ev.index, ev.kind, ev.delta);
        if (!parts) return false;
        set((s) => ({ live: { ...s.live, [id]: { ...cur, parts, seq: ev.seq } } }));
        return true;
      }
      case "gen.parts":
        if (cur && ev.seq <= cur.seq) return true;
        set((s) => ({ live: { ...s.live, [id]: { ...base, ...cur, parts: ev.parts, seq: ev.seq, status: "streaming" } } }));
        return true;
      case "gen.done":
        set((s) => ({
          live: {
            ...s.live,
            [id]: { ...base, ...cur, parts: ev.parts, seq: ev.seq, status: ev.status, error: ev.error, usage: ev.usage },
          },
          byTemp: ev.temp_id ? { ...s.byTemp, [ev.temp_id]: id } : s.byTemp,
        }));
        return true;
    }
    return true;
  },
  forget: (id) =>
    set((s) => {
      const live = { ...s.live };
      delete live[id];
      return { live };
    }),
}));

/** A stored message with any live state applied on top. */
export function withLive(m: Message, live: Live | undefined): Message {
  if (!live) return m;
  if (m.status !== "streaming" && live.status === "streaming") return m;
  return { ...m, parts: live.parts, status: live.status, error: live.error ?? m.error, usage: live.usage ?? m.usage };
}
